import { useTranslations } from 'next-intl'
import { z } from 'zod'

export const MAX_TITLE_LENGTH = 30
export const MAX_CONCURRENT_USERS = 40
export const DEFAULT_PREFERRED_DEVICES = ['android', 'desktop', 'hmd', 'ios']
export const DEVICE_LABEL_MAP = {
  desktop: 'PC',
  ios: 'iPhone/ iPad',
  android: 'Android phone/ pad',
  hmd: 'VR',
}

export const useWorldSettingsFormSchema = () => {
  const t = useTranslations()

  const worldSettingsFormSchema = z
    .object({
      title: z
        .string()
        .trim()
        .min(1, { message: t('profile-page.my-worlds-card-settings-validate-title') })
        .max(MAX_TITLE_LENGTH),
      genre: z
        .array(z.string())
        .min(1, { message: t('profile-page.my-worlds-card-settings-validate-genre') })
        .optional(),
      policy: z.union([z.literal('public'), z.literal('private'), z.literal('unlisted')]),
      passcode: z.string(),
      enablePasscode: z.boolean(),
      description: z.string().optional(),
      concurrentUsers: z.number().min(1).max(MAX_CONCURRENT_USERS),
      preferredDevices: z
        .array(z.string())
        .min(1, { message: t('spaces-create-modal.error.device-filter') }),
      isAdultOnly: z.boolean(),
      // coOwners: z
      //   .array(
      //     z.object({
      //       id: z.string(),
      //       display_name: z.string(),
      //     }),
      //   )
      //   .max(5)
      //   .default([]),
      trailerMedia: z.object({
        trailer_videos: z
          .array(
            z.union([
              z.instanceof(File),
              z.object({
                vod_id: z.string(),
                file_id: z.string(),
                cover_image: z.string().optional(),
              }),
            ]),
          )
          .optional()
          .nullable(),
        trailer_images: z
          .array(
            z.union([
              z.instanceof(File),
              z.object({
                id: z.string().optional(),
                url: z.string().optional(),
              }),
            ]),
          )
          .optional()
          .nullable(),
      }),
      logoOverlayPosition: z.union([
        z.literal('top-left'),
        z.literal('top-center'),
        z.literal('top-right'),
        z.literal('bottom-left'),
        z.literal('bottom-center'),
        z.literal('bottom-right'),
      ]),
      githubUrl: z.string().optional(),
    })
    .superRefine(({ enablePasscode, passcode, policy, title, genre, trailerMedia }, ctx) => {
      if (enablePasscode && !/^[0-9*]{6}$/.test(passcode)) {
        ctx.addIssue({
          code: 'custom',
          message: t('profile-page.my-worlds-card-settings-validate-passcode'),
          path: ['passcode'],
        })
      }

      if (!title) {
        ctx.addIssue({
          code: 'custom',
          message: t('profile-page.my-worlds-card-settings-validate-title'),
          path: ['title'],
        })
      }

      if (!genre || genre.length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: t('profile-page.my-worlds-card-settings-validate-genre'),
          path: ['genre'],
        })
      }

      if (policy === 'public') {
        if (!trailerMedia?.trailer_videos?.length && !trailerMedia?.trailer_images?.length) {
          ctx.addIssue({
            code: 'custom',
            message: t('profile-page.my-worlds-card-settings-trailer-error-message'),
            path: ['trailerMedia'],
          })
        }
      }
    })

  return worldSettingsFormSchema
}

export type WorldSettingsFormValues = z.infer<ReturnType<typeof useWorldSettingsFormSchema>>
